import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardImg,
  CardTitle,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CourseForm from "../component/CourseForm";
import { Link, useNavigate } from "react-router-dom";
import img2 from "../../../assets/images/small/img-2.jpg";
import axios from "axios";
import CourseService from "../../../services/KangarooServices/CourseService";
import CourseDetail from "../component/CourseDetail";
import {
  formatDate,
  formatDateTime,
} from "../../../Components/Common/FormatDate";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import { imageBaseApi, smartkampus } from "../../../utilis/imageHelper";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Select from "react-select";

const SortableCard = ({ item, onEdit, onDelete, onPublishClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className="h-100 shadow-sm border-0 rounded-4 small-card cursor-pointer"
        style={{
          border: isDragging
            ? "2px dashed #007bff"
            : isOver
            ? "2px solid #28a745"
            : "1px solid #ddd",
          backgroundColor: isDragging ? "#e7f1ff" : isOver ? "#e6ffe6" : "#fff",
          borderRadius: 12,
          boxShadow: isDragging
            ? "0 12px 24px rgba(0,0,0,0.25)"
            : "0 2px 5px rgba(0,0,0,0.1)",
          transition:
            "background-color 150ms ease, box-shadow 150ms ease, border 150ms ease",
        }}
      >
        <div
          {...listeners}
          {...attributes}
          className="h-100"
          style={{ cursor: "grab" }}
        >
          {item.bannerMedia?.url && (
            <div className="logo-wrapper">
              <CardImg
                src={`${imageBaseApi}${item.bannerMedia.url}`}
                alt={item.name}
                className="img-fluid h-100 w-100"
                style={{ objectFit: "cover" }}
              />
            </div>
          )}

          <CardBody className="py-2 px-3">
            <div className="d-flex justify-content-between">
              {item.courseCode && (
                <p className="mb-1 text-secondary">
                  <small>Code:</small> {item.courseCode}
                </p>
              )}
              <p className="mb-1 text-secondary">
                <small>Modules:</small> {item.childrenCount || 0}
              </p>
            </div>
            <p className="mb-0 fw-semibold">{item.name}</p>
          </CardBody>
        </div>

        <CardFooter className="bg-transparent border-0 pt-0 pb-3 px-3 d-flex justify-content-between align-content-center align-items-center">
          <Button
            color={item?.isPublished ? "success" : "danger"}
            onClick={() => onPublishClick(item)}
            size="sm"
          >
            {item?.isPublished ? "Published" : "Draft"}
          </Button>

          <div className="d-flex gap-1">
            <a
              href={`${smartkampus}/course/${item?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <Button size="sm" color="info" title="Preview">
                <i className="ri-eye-line"></i>
              </Button>
            </a>

            <Button size="sm" color="warning" onClick={() => onEdit(item)}>
              <i className="ri-edit-line" />
            </Button>

            <Button size="sm" color="danger" onClick={() => onDelete(item)}>
              <i className="ri-delete-bin-5-line" />
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

const Course = () => {
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState([]);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [publishModal, setPublishModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize] = useState(12);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupOptions, setGroupOptions] = useState([]);

  // Use a ref to prevent multiple simultaneous fetches
  const isFetching = useRef(false);

  const togglePublishModal = () => setPublishModal((p) => !p);
  const toggleDelete = () => setIsOpenDelete((p) => !p);

  // ─── Fetch course groups ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await CourseService.getGrpList();
        setGroupOptions(
          response.map((group) => ({
            label: group.headingName,
            value: group.id,
          })),
        );
      } catch (error) {
        console.error(error);
      }
    };
    fetchGroups();
  }, []);

  // ─── Fetch courses ──────────────────────────────────────────────────────────
  const fetchData = async (page, size, search, groupId) => {
    if (isFetching.current) return;
    isFetching.current = true;
    try {
      const data = await CourseService.getList({
        search,
        page,
        pageSize: size,
        courseGroupId: groupId ?? null,
      });
      // Deduplicate by id before storing
      const seen = new Set();
      const unique = (data.data || []).filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      setCourseData(unique);
      setTotalRows(data.totalRecords || 0);
    } catch (err) {
      console.error(err);
    } finally {
      isFetching.current = false;
    }
  };

  // Reset to page 1 when search or group filter changes
  useEffect(() => {
    setCurrentPage(1);
    fetchData(1, pageSize, searchTerm, selectedGroup?.courseGroupId);
  }, [searchTerm, selectedGroup]);

  // Fetch when page changes (but not when search/group change — those reset page above)
  useEffect(() => {
    fetchData(currentPage, pageSize, searchTerm, selectedGroup?.courseGroupId);
  }, [currentPage]);

  // ─── Group courses (no duplicates) ─────────────────────────────────────────
  const groupedCourses = useMemo(() => {
    const seen = new Set();
    const acc = {};

    for (const course of courseData) {
      // Skip duplicate course ids
      if (seen.has(course.id)) continue;
      seen.add(course.id);

      const groupId = course.coursesGroupId ?? "ungrouped";
      if (!acc[groupId]) {
        acc[groupId] = {
          name: course.coursesGroupName ?? "Ungrouped",
          items: [],
        };
      }
      acc[groupId].items.push(course);
    }
    return acc;
  }, [courseData]);

  // ─── Drag and drop ──────────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeGroupId = Object.keys(groupedCourses).find((gid) =>
      groupedCourses[gid].items.some((i) => i.id === active.id),
    );
    const overGroupId = Object.keys(groupedCourses).find((gid) =>
      groupedCourses[gid].items.some((i) => i.id === over.id),
    );

    if (!activeGroupId || !overGroupId || activeGroupId !== overGroupId) {
      toast.error("Courses can only be reordered within the same group.");
      return;
    }

    setCourseData((prevItems) => {
      const oldIndex = prevItems.findIndex((i) => i.id === active.id);
      const newIndex = prevItems.findIndex((i) => i.id === over.id);
      const newItems = arrayMove(prevItems, oldIndex, newIndex);

      // Get the new order for this group only
      const groupItems = newItems.filter(
        (i) => String(i.coursesGroupId) === String(activeGroupId),
      );

      // Update orderIndex for every item in the group
      groupItems.forEach((item, index) => {
        CourseService.updateCourse(item.id, {
          name: item.name,
          isPublished: item.isPublished,
          orderIndex: index,
        });
      });

      return newItems;
    });
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────
  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (!selectedDeleteItem) return;
    try {
      await CourseService.delete(selectedDeleteItem.id);
      toast.success("Course deleted successfully");
      await fetchData(
        currentPage,
        pageSize,
        searchTerm,
        selectedGroup?.courseGroupId,
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete Course");
    } finally {
      toggleDelete();
      setSelectedDeleteItem(null);
    }
  };

  // ─── Publish ────────────────────────────────────────────────────────────────
  const handleConfirmPublish = async () => {
    if (!selectedItem) return;
    setLoading(true);
    try {
      const response = await CourseService.updatePublishStatus(
        selectedItem.id,
        !selectedItem.isPublished,
      );
      toast.success("Course status updated successfully done.");
      await fetchData(
        currentPage,
        pageSize,
        searchTerm,
        selectedGroup?.courseGroupId,
      );
      togglePublishModal();
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalRows / pageSize);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Courses" pageTitle="Course Management" />

        <Row className="mb-3">
          <Col>
            <Button color="success" onClick={() => navigate("/course/create")}>
              + Add Course
            </Button>
          </Col>

          <Col xs={7} lg={4}>
            <Select
              options={groupOptions}
              value={
                groupOptions.find(
                  (o) => o.value === selectedGroup?.courseGroupId,
                ) || null
              }
              onChange={(selected) =>
                setSelectedGroup(
                  selected ? { courseGroupId: selected.value } : null,
                )
              }
              placeholder="Select course group"
              isClearable
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Col>
          <Col className="d-flex justify-content-end">
            <Input
              type="text"
              placeholder="Search by name or course"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
        </Row>
        <Col>
          <p className="text-muted font-bold">
            Drag the tiles to reorder the courses.
          </p>
        </Col>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {Object.entries(groupedCourses).map(([groupId, group]) => (
            <div key={groupId} className="mb-5">
              <h5 className="mb-3 fw-bold">{group.name}</h5>
              <SortableContext
                items={group.items.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {group.items.map((item) => (
                    <SortableCard
                      key={item.id}
                      item={item}
                      onEdit={(item) => navigate(`/course/update/${item.id}`)}
                      onDelete={handleDeleteClick}
                      onPublishClick={(item) => {
                        setSelectedItem(item);
                        togglePublishModal();
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </div>
          ))}
        </DndContext>

        {totalPages > 1 && (
          <div className="d-flex justify-content-center my-4">
            <Pagination>
              <PaginationItem disabled={currentPage === 1}>
                <PaginationLink
                  previous
                  onClick={() => handlePageChange(currentPage - 1)}
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <PaginationItem key={page} active={page === currentPage}>
                    <PaginationLink onClick={() => handlePageChange(page)}>
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem disabled={currentPage === totalPages}>
                <PaginationLink
                  next
                  onClick={() => handlePageChange(currentPage + 1)}
                />
              </PaginationItem>
            </Pagination>
          </div>
        )}

        <DeleteModal
          show={isOpenDelete}
          onCloseClick={toggleDelete}
          onDeleteClick={handleDelete}
        />

        <Modal isOpen={publishModal} toggle={togglePublishModal} centered>
          <ModalHeader toggle={togglePublishModal}>Confirm Action</ModalHeader>
          <ModalBody>
            {selectedItem?.isPublished
              ? "Are you sure you want to move this course to Draft?"
              : "Are you sure you want to Publish this course?"}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={togglePublishModal}>
              Cancel
            </Button>
            <Button
              color={selectedItem?.isPublished ? "danger" : "success"}
              onClick={handleConfirmPublish}
              disabled={loading}
            >
              {loading ? "Processing..." : "Yes, Confirm"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </Container>
  );
};

export default Course;
